/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, inject } from '@angular/core';
import { ActiveState, applyTransaction, EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { initializeCombinedHousingExpenses } from '../../cmg-1003/components/employment-income-expenses/expenses/expenses.util';
import {
  ApplicationInfoModel,
  BorrowerModel,
  EmploymentIncomeExpenseModel,
  AssetLiabilitiesModel,
  PropertyTaxCalculationType,
} from '../../cmg-1003/models';
import { SessionStore } from '../../core/state';

import { Cmg1003AssetsStore } from '../assetLiabilities/assetLiabilities.store';
import { Cmg1003BorrowerStore } from '../borrower/borrower.store';
import { Cmg1003DeclarationsStore } from '../declarations/declarations.store';
import { Cmg1003EmploymentIncomeExpensesStore } from '../employment-income-expenses/employment-income-expenses.store';
import { Cmg1003GmiStore } from '../gmi/gmi.store';

export interface Cmg1003ApplicationInfoState extends EntityState<ApplicationInfoModel, number>, ActiveState {}

function createInitialState(): Cmg1003ApplicationInfoState {
  return <Cmg1003ApplicationInfoState>{};
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'cmg-1003-app-info-store', idKey: 'applicationId' })
export class Cmg1003ApplicationInfoStore extends EntityStore<Cmg1003ApplicationInfoState> {
  private readonly _borrowerStore = inject(Cmg1003BorrowerStore);
  private readonly _declarationsStore = inject(Cmg1003DeclarationsStore);
  private readonly _empIncExpStore = inject(Cmg1003EmploymentIncomeExpensesStore);
  private readonly _gmiStore = inject(Cmg1003GmiStore);
  private readonly _assetsStore = inject(Cmg1003AssetsStore);
  private readonly _sessionStore = inject(SessionStore);

  constructor() {
    super(createInitialState());
  }

  /**
   * Adds a new Application and does not set it to active.
   * @param autoRecoveredNewApp Optional. A new application that was captured during auto-recovery and NOT yet saved to Byte. Should still have negative IDs.
   * @returns
   */
  addNewApplication(propertyTaxCalcType: PropertyTaxCalculationType, autoRecoveredNewApp?: ApplicationInfoModel) {
    // Application
    const blankApplication = new ApplicationInfoModel();

    // [Scenario]: Creating a brand new app for the first time.
    if (!autoRecoveredNewApp || Object.keys(autoRecoveredNewApp)?.length === 0) {
      blankApplication.applicationId = -CmgUiUtilities.getUniqueNumber();
      blankApplication.additionalBorrowerChecked = false;
      blankApplication.borrowerId = -CmgUiUtilities.getUniqueNumber();
      blankApplication.coBorrowerId = -CmgUiUtilities.getUniqueNumber();
    }
    // [Scenario]: Re-creating a new app captured by Auto-Recovery - "Unsaved Changes".
    else {
      blankApplication.applicationId = autoRecoveredNewApp.applicationId;
      blankApplication.additionalBorrowerChecked = autoRecoveredNewApp.additionalBorrowerChecked;
      blankApplication.borrowerId = autoRecoveredNewApp.borrowerId;
      blankApplication.coBorrowerId = autoRecoveredNewApp.coBorrowerId;
    }

    // Primary Borrower
    const borrowerModel = BorrowerModel.createNewUntypedAdditionalBorrower(blankApplication.applicationId, blankApplication.borrowerId);
    this.add(blankApplication); // application needs to be added first
    borrowerModel.isPrimaryBorrower = true;
    this._borrowerStore.add(borrowerModel);

    // Co-Borrower
    const coBorrowerModel = BorrowerModel.createNewUntypedAdditionalBorrower(blankApplication.applicationId, blankApplication.coBorrowerId);
    coBorrowerModel.isPrimaryBorrower = false;
    this._borrowerStore.add(coBorrowerModel);

    // Employment, Income & Expenses
    const blankEmpIncExpModel: EmploymentIncomeExpenseModel = {
      applicationId: blankApplication.applicationId,
      coBorrowerEmployers: [],
      borrowerEmployers: [],
      combinedHousingExpenses: initializeCombinedHousingExpenses(propertyTaxCalcType),
    };

    this._empIncExpStore.add(blankEmpIncExpModel);

    // Declarations
    [borrowerModel, coBorrowerModel].forEach((b) => {
      this._declarationsStore.add({ borrowerId: b.borrowerId });
    });

    // GMI
    [borrowerModel, coBorrowerModel].forEach((b) => {
      this._gmiStore.add({ borrowerId: b.borrowerId, infoProvidedVia: 0 });
    });

    // assets
    this._assetsStore.add(AssetLiabilitiesModel.createEmpty(blankApplication.applicationId));

    return blankApplication.applicationId;
  }

  removeApplication(oldApplicationId: number) {
    // only applications that have not yet been saved can be removed.
    if (oldApplicationId < 0) {
      // apply transaction to avoid any unwanted side effect
      applyTransaction(() => {
        this._assetsStore.remove(oldApplicationId);

        const appBorrowerId = this.getValue().entities[oldApplicationId].borrowerId;
        const appCoBorrowerId = this.getValue().entities[oldApplicationId].coBorrowerId;
        this._borrowerStore.remove([appBorrowerId, appCoBorrowerId]);
        this._declarationsStore.remove([appBorrowerId, appCoBorrowerId]);
        this._gmiStore.remove([appBorrowerId, appCoBorrowerId]);
        this._empIncExpStore.remove(oldApplicationId);
        this.remove(oldApplicationId);
      });
    }
  }

  override akitaPreAddEntity = (application: ApplicationInfoModel) => {
    application.additionalBorrowerChecked = !CmgUiUtilities.isIdTemporary(application.applicationId); // assume additional borrower is there
    if (application.coBorrowerId === null || typeof application.coBorrowerId === 'undefined') {
      const tempSecondaryBorrowerId = CmgUiUtilities.getTemporaryBorrowerId(application.borrowerId);
      // if there is only primary borrower
      application.coBorrowerId = tempSecondaryBorrowerId;
      application.additionalBorrowerChecked = false;
      const newAdditionalBorrowerModel = BorrowerModel.createNewUntypedAdditionalBorrower(application.applicationId, tempSecondaryBorrowerId);
      newAdditionalBorrowerModel.isPrimaryBorrower = false;
      this._borrowerStore.add(newAdditionalBorrowerModel);

      // Create temp entities, related to temp borrower.
      [this._declarationsStore, this._gmiStore].forEach((store) => {
        store.add({ borrowerId: tempSecondaryBorrowerId });
      });
    }

    return application;
  };

  setAdditionalBorrowerChecked(applicationId: number, additionalBorrowerChecked: boolean) {
    this.update(applicationId, (entity) => ({ additionalBorrowerChecked: additionalBorrowerChecked }));
  }

  setCreditReferenceNumber(applicationId: number, creditReferenceNo: string) {
    this.update(applicationId, (entity) => ({ creditRefNo: creditReferenceNo }));
  }

  override akitaPreUpdateEntity(_: Readonly<ApplicationInfoModel>, nextEntity: any): ApplicationInfoModel {
    this._sessionStore.update({ lastUpdatedDateTime: Date.now() });
    return nextEntity;
  }
}
